import moment from 'moment';

/**
 * ChatbotService - OpenAI integration for Mike AI Assistant
 *
 * Supports three modes:
 * - Meijer on dashboard: Hybrid mode with both dashboard data and work order summary
 * - Meijer (other pages): Work order analysis with focus on compressor failures
 * - Non-Meijer: Company Admin Dashboard data analysis
 *
 * Calls OpenAI API directly from the browser with appropriate context.
 * Model and context budget are configurable via environment variables.
 */
class ChatbotService {
  static OPENAI_PROXY_URL = process.env.REACT_APP_OPENAI_PROXY_URL;
  static MODEL = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4.1-mini';
  static CONTEXT_BUDGET = parseInt(process.env.REACT_APP_CHATBOT_CONTEXT_BUDGET || '120000', 10);

  /**
   * Returns a preamble placed at the TOP of every system prompt to prevent hallucinations.
   * Position matters — LLMs weight instructions at the beginning most heavily.
   */
  static getAntiHallucinationPreamble(context) {
    const isMeijer = context.user?.companyId === "1121";
    const isMeijerDashboard = isMeijer && context.isMeijerDashboard === true;
    const isLoading = context._dataLoading === true;

    // Build explicit data scope boundaries
    let scopeLines = '';
    if (isMeijer && !isMeijerDashboard) {
      scopeLines = `
DATA SCOPE: You ONLY have access to Meijer work order data for the specified date range.
You do NOT have access to: dashboard analytics, active users, MSI devices, property inspections, critical inspections, reports, or tasking sheets.
If a user asks about any of those topics, say: "I don't have access to dashboard data on this page. You can find that information on the Company Admin Dashboard."`;
    } else if (isMeijer && isMeijerDashboard) {
      scopeLines = `
DATA SCOPE: You have access to dashboard analytics AND work order summary statistics (not individual work order details).
You do NOT have access to: individual work order records, specific work order numbers, or per-work-order assignee details on this page.
If the user needs individual work order details, say: "On the dashboard I only have summary statistics. Please navigate to another page for detailed work order analysis."`;
    } else {
      scopeLines = `
DATA SCOPE: You ONLY have access to Company Admin Dashboard data for the specified date range.
You do NOT have access to: work orders, compressor failure data, or any data outside this dashboard.
If a user asks about topics outside dashboard analytics, say: "I specialize in dashboard data analysis. I don't have access to that information."`;
    }

    // Loading state warning
    let loadingNote = '';
    if (isLoading) {
      loadingNote = `
DATA STATUS: Data is currently LOADING. Do NOT answer questions about the data until loading is complete. Tell the user: "Your data is still loading. Please wait a moment and ask again."`;
    }

    return `=== CRITICAL INSTRUCTIONS — READ FIRST ===
NEVER fabricate, guess, or infer information that is not explicitly present in the data provided below.
If you are unsure, if the data is ambiguous, or if you do not have enough information to answer confidently, you MUST either:
  1. Ask the user a clarifying question to narrow down what they need, OR
  2. Clearly state what you do not know and why.

It is ALWAYS better to ask a clarifying question than to guess. Examples of correct behavior:
  - "I see two fields that could match — could you clarify whether you mean X or Y?"
  - "I don't have data on that topic. I can help you with [list what you CAN do]."

When data has been truncated (e.g., "23 of 156 items"), do NOT extrapolate totals, averages, or trends from the partial subset. State that you are working with a partial view and provide analysis only for the items shown. This truncation rule applies ONLY when a section header in the data context below explicitly states truncation (e.g., "83 of 620 shown"). Otherwise treat the data as complete and authoritative.
${scopeLines}${loadingNote}
=== END CRITICAL INSTRUCTIONS ===

`;
  }

  /**
   * Returns critical behavioral rules placed near the TOP of every system prompt.
   * Addresses specific failure modes observed in production conversations:
   *   - Missing items in long lists (LLM attention dropping mid-scan)
   *   - Fabricating exclusions to justify wrong counts
   *   - Applying subjective judgment to deterministic fields
   *   - Asking the user to provide data already in context
   *   - Property number formatting clarification dances
   */
  static getCriticalBehavioralRules() {
    return `=== CRITICAL BEHAVIORAL RULES ===

EXHAUSTIVE LISTING:
When asked to list ALL items of a certain type, you MUST include EVERY matching item. Do NOT truncate or summarize unless the user explicitly asks for a subset (e.g., "top 10"). If you state there are N items, list all N. For compressor failures specifically: read directly from the dedicated "COMPRESSOR FAILURE WORK ORDERS" section in the data context — it is a complete, pre-filtered list. Copy every item from that section verbatim; do NOT re-scan the broader work order list looking for additional compressor failures (there are none beyond the dedicated section).

ANTI-FABRICATION:
If a previous response gave a count or list that does not match the underlying data, simply re-output the corrected version directly from the data section. NEVER invent exclusions, reclassification, ambiguity, or "low confidence" reasons to justify a previous wrong answer. The correct response to "you missed some" is to re-read the relevant section and re-list everything completely — not to scan candidates aloud, mark items as "exclude", or guess at what you might have missed.

NO INVENTED IDENTIFIERS:
Never output a work order number (e.g., "MEJR123456789"), property number, asset ID, equipment ID, or any other identifier unless that exact identifier appears verbatim in the data context below. Do not generate plausible-looking IDs from the pattern of real ones. If you can't find a specific record the user asked about, say "I don't see that work order in the current data" — do NOT invent or speculate about identifiers. Also do NOT claim a specific identifier is "not found" unless the user gave you a specific identifier to look up.

TRUST DETERMINISTIC FIELDS:
\`compressor_failure_detected\` is a boolean field already computed by the data layer. A work order is a compressor failure if and only if this field is true. Do NOT re-evaluate based on text in the task, notes, or reasoning fields. Do NOT filter by "confidence", "explicit mention", or "ambiguity" — that classification has already been made. Report what the field says.

DO NOT ASK FOR DATA ALREADY LOADED:
Never ask the user to export, paste, upload, or otherwise re-provide data that is already in this prompt. If a work order list (or any data section) is in the context below, work from it. The data is authoritative — do not solicit replacements or claim incompleteness unless a section header explicitly states truncation.

PROPERTY NUMBER NORMALIZATION:
Property numbers in user requests may be missing leading zeros (e.g., "27A" vs. "0027A" in the data, or "109A" vs. "0109A"). When matching property numbers, normalize by zero-padding the numeric portion to 4 digits before comparing. Do this silently — do not ask the user to clarify formatting unless their input is genuinely unparseable.

FILTERING AND CROSS-REFERENCING:
When the user asks you to filter records or cross-reference against a user-provided list:

1. Normalize user input first (per PROPERTY NUMBER NORMALIZATION rule).

2. If the user's filter list has 6 or more entries, echo your normalized version before showing matches. Example: "Normalizing 45 properties → 0027A, 0032A, 0034A, ..., 0680A, 0681A. Checking against compressor failures..." This makes the filter step visible to the user and forces explicit attention to each entry.

3. Iterate through the SOURCE RECORDS, not the filter list. For each compressor failure (or other source record) one at a time, check whether its field is in the user's list. This direction is more reliable than iterating through the filter list and searching records for each entry — the source set is smaller and focal.

4. After showing matches, do NOT add a confident "no other items match" or "no further matches exist" claim. Use language like "These are the matches I found in the COMPRESSOR FAILURE WORK ORDERS section" — accurate without overclaiming completeness.

=== END CRITICAL BEHAVIORAL RULES ===

`;
  }

  /**
   * Send a message to the chatbot with work order context
   *
   * @param {string} userMessage - The user's question or message
   * @param {Object} context - Context object with work orders only
   * @param {Array} conversationHistory - Previous messages in the conversation
   * @param {boolean} isWelcome - Whether this is the initial welcome message
   * @returns {Promise<Object>} { reply: string, error: string|null }
   */
  static async sendMessage(userMessage, context, conversationHistory = [], isWelcome = false) {
    try {
      if (!this.OPENAI_PROXY_URL) {
        console.error('[ChatbotService] OPENAI_PROXY_URL not configured');
        return {
          reply: null,
          error: 'Chatbot proxy URL not configured. Please add REACT_APP_OPENAI_PROXY_URL to your .env file.'
        };
      }

      // Determine company type and page context
      const isMeijer = context.user?.companyId === "1121";
      const isMeijerDashboard = isMeijer && context.isMeijerDashboard === true;

      // Build system prompt with context based on company type and page
      const composeStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const systemPrompt = isWelcome
        ? (isMeijerDashboard
            ? this.buildMeijerDashboardWelcomeSystemPrompt(context)
            : isMeijer
              ? this.buildWelcomeSystemPrompt(context)
              : this.buildNonMeijerWelcomeSystemPrompt(context))
        : (isMeijerDashboard
            ? this.buildMeijerDashboardSystemPrompt(context, userMessage)
            : isMeijer
              ? this.buildSystemPrompt(context, userMessage)
              : this.buildNonMeijerSystemPrompt(context, userMessage));

      // Format conversation history for OpenAI
      const formattedHistory = conversationHistory
        .filter(msg => !msg.isWelcome) // Exclude welcome messages from history
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));

      const promptChars = systemPrompt.length + (formattedHistory.reduce((acc, m) => acc + (m.content?.length || 0), 0) || 0) + (userMessage?.length || 0);
      const approxTokens = Math.round(promptChars / 4); // rough heuristic
      const composeEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();

      console.log('[Mike][ChatbotService]', `Sending ${isWelcome ? 'welcome' : 'message'} to OpenAI...`);
      try {
        const contextSize = JSON.stringify(context).length;
        console.log('[Mike][ChatbotService]', `Context size: ${contextSize.toLocaleString()} chars, prompt ~${approxTokens.toLocaleString()} tokens, history: ${formattedHistory.length} msgs, compose ${(composeEnd - composeStart).toFixed(1)}ms`);
      } catch (e) {
        console.log('[Mike][ChatbotService]', 'Context size: <unavailable>', e?.message);
      }
      console.log('[Mike][ChatbotService]', 'User message preview:', (userMessage || '').slice(0, 200));

      // Call OpenAI API
      const networkStart = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const response = await fetch(this.OPENAI_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: this.MODEL,
          messages: [
            { role: 'system', content: systemPrompt },
            ...formattedHistory,
            { role: 'user', content: userMessage }
          ],
          max_completion_tokens: isWelcome
            ? parseInt(process.env.REACT_APP_OPENAI_WELCOME_MAX_TOKENS || '2000', 10)
            : parseInt(process.env.REACT_APP_OPENAI_MAX_TOKENS || '4000', 10)
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[ChatbotService] OpenAI API error:', errorData);
        const networkEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();
        console.log('[Mike][ChatbotService]', `OpenAI request failed: ${response.status} ${response.statusText}, network ${(networkEnd - networkStart).toFixed(1)}ms`);
        console.log('[Mike][ChatbotService]', 'Error message:', errorData?.error?.message || 'n/a');

        if (response.status === 401) {
          return { reply: null, error: 'Invalid OpenAI API key. Please check your configuration.' };
        } else if (response.status === 429) {
          return { reply: null, error: 'Rate limit exceeded. Please wait a moment and try again.' };
        } else {
          return { reply: null, error: `OpenAI API error: ${response.status} ${response.statusText}` };
        }
      }

      const data = await response.json();
      const reply = data.choices[0].message.content;
      const networkEnd = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const usage = data?.usage ? `; usage prompt ${data.usage.prompt_tokens} / completion ${data.usage.completion_tokens}` : '';
      console.log('[Mike][ChatbotService]', `Received response (${reply.length.toLocaleString()} chars) in ${(networkEnd - networkStart).toFixed(1)}ms${usage}`);

      return { reply, error: null };

    } catch (error) {
      console.error('[ChatbotService] Error calling OpenAI API:', error);
      return {
        reply: null,
        error: `Failed to connect to OpenAI: ${error.message}`
      };
    }
  }

  /**
   * Build system prompt for welcome message (work orders only, no view mode)
   */
  static buildWelcomeSystemPrompt(context) {
    const compressorFailures = (context.workOrders || []).filter(wo => wo.compressor_failure_detected).length;

    // Calculate status breakdown
    const statusCounts = (context.workOrders || []).reduce((acc, wo) => {
      const status = wo.status_id || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const totalCost = (context.workOrders || []).reduce((sum, wo) => sum + (parseFloat(wo.costs_total_value) || 0), 0);

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a specialized AI assistant for analyzing Meijer work orders and compressor failures in the DST Platform.

Your role: Help users analyze work orders with a focus on compressor failures, costs, patterns, and maintenance trends.

Generate a warm welcome message that:
1. Introduces yourself as a Meijer work order specialist (1-2 friendly sentences)
2. Summarizes current work order data with emphasis on compressor failures
3. Provides 5 specific, actionable questions about work orders and compressors

Guidelines for the welcome message:
- Be warm, conversational, and professional
- Format clearly with sections using separator lines (━━━━━━━━━━)
- Highlight compressor failures prominently if any exist
- Make metrics specific to the actual work order data
- Suggest practical questions about costs, failures, and trends
- Keep under 300 words
- End with an inviting question like "What would you like to know?"
- Use professional formatting: bullet points, bold text, numbered lists (no markdown tables)
- Use exact status names (Open, New, Completed, Paused, Cancelled, InProgress) — show all statuses, never collapse to "open/closed"
- Do NOT use emojis - maintain a professional business tone

Current Context:
- User: ${context.user.name} (Meijer)
- Date Range: ${moment(context.dateRange.start).format('MMM D, YYYY')} to ${moment(context.dateRange.end).format('MMM D, YYYY')}
- Total Work Orders: ${context.totalWorkOrders}
- Compressor Failures Detected: ${compressorFailures}
- Status Breakdown: ${Object.entries(statusCounts).map(([status, count]) => `${status}: ${count}`).join(', ')}
- Total Costs: $${totalCost.toLocaleString()}

Format Structure:
Hello! [Brief greeting as work order specialist]

I can help you analyze work orders and compressor failures for your Meijer operations.

Work Order Summary (Date Range):
━━━━━━━━━━━━━━━━━━━━━━━━
• Date Range: [start] to [end]
• Total Work Orders: [count]
• Compressor Failures: [count]${compressorFailures > 0 ? ' (ACTION REQUIRED)' : ''}
• Status Breakdown:
  - [Status]: [count] (list all statuses)
• Total Costs: $[amount]

You can ask me about:
  • [Question about compressor failures]
  • [Question about costs]
  • [Question about compressor failure patterns]
  • [Question about trends]
  • [Question about specific work orders]

[Closing invitation]`;
  }

  /**
   * Build system prompt for Meijer welcome message on Company Admin Dashboard
   * Hybrid: dashboard data + work order summary
   */
  static buildMeijerDashboardWelcomeSystemPrompt(context) {
    const summary = context.workOrderSummary || {};
    const dataFetched = context.dashboardDataFetched || {};

    // Format favorites info
    const favoritesInfo = context.favorites?.count > 0
      ? `${context.favorites.count} favorite properties: ${context.favorites.propertyNames.slice(0, 5).join(', ')}${context.favorites.count > 5 ? '...' : ''}`
      : 'No favorite properties set';

    // Format property lists info
    const propertyListsInfo = context.propertyLists?.count > 0
      ? `${context.propertyLists.count} property lists: ${context.propertyLists.lists.join(', ')}`
      : 'No property lists available';

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a specialized AI assistant for the DST Platform, helping Meijer users on the Company Admin Dashboard.

You have access to BOTH dashboard analytics data AND work order summary statistics. Your role is to help users explore both data sources.

Generate a warm welcome message that:
1. Introduces yourself and explains you have access to both dashboard data and work orders
2. Shows a brief work order summary (total, compressor failures, costs)
3. Shows which dashboard data cards have been loaded
4. Asks what the user would like to explore
5. Provides example questions spanning both data sources

Current Context:
- User: ${context.user?.name || 'User'} (Meijer)
- Date Range: ${moment(context.dateRange?.start).format('MMM D, YYYY')} to ${moment(context.dateRange?.end).format('MMM D, YYYY')}
- ${favoritesInfo}
- ${propertyListsInfo}

Work Order Summary:
- Total Work Orders: ${summary.total || 0}
- Compressor Failures: ${summary.compressorFailures || 0}${summary.compressorFailures > 0 ? ' (ACTION REQUIRED)' : ''}
- Status Breakdown: ${Object.entries(summary.statusBreakdown || {}).map(([s, c]) => `${s}: ${c}`).join(', ') || 'N/A'}
- Total Costs: $${(summary.totalCost || 0).toLocaleString()}

Dashboard Data Loading Status:
- Active Users: ${dataFetched.activeUsers ? 'Loaded' : 'Not loaded yet'}
- MSI Used: ${dataFetched.msiUsed ? 'Loaded' : 'Not loaded yet'}
- Properties Inspected: ${dataFetched.propertiesInspected ? 'Loaded' : 'Not loaded yet'}
- Critical Inspections: ${dataFetched.criticalInspections ? 'Loaded' : 'Not loaded yet'}
- Reports: ${dataFetched.reports ? 'Loaded' : 'Not loaded yet'}
- Tasking Sheets: ${dataFetched.taskingSheets ? 'Loaded' : 'Not loaded yet'}
- All Company Properties: ${dataFetched.allCompanyProperties ? 'Loaded' : 'Not loaded yet'}

Guidelines:
- Be warm, conversational, and professional
- Format clearly with sections using separator lines (━━━━━━━━━━)
- Show work order summary first, then dashboard data status
- Suggest questions from BOTH domains (work orders AND dashboard)
- Do NOT use emojis - maintain a professional business tone
- Keep under 300 words
- End by asking what they'd like to explore`;
  }

  /**
   * Build system prompt for Meijer regular messages on Company Admin Dashboard
   * Hybrid: dashboard data + work order summary
   */
  static buildMeijerDashboardSystemPrompt(context, userMessage) {
    const summary = context.workOrderSummary || {};
    const dataFetched = context.dashboardDataFetched || {};
    const dashboardData = context.dashboardData || {};

    console.log('[Mike][ChatbotService]', 'buildMeijerDashboardSystemPrompt()', {
      user: `${context.user?.name} (${context.user?.companyName})`,
      dateRange: `${moment(context.dateRange?.start).format('YYYY-MM-DD')} -> ${moment(context.dateRange?.end).format('YYYY-MM-DD')}`,
      workOrderSummary: summary,
      dataFetched
    });
    if (userMessage) console.log('[Mike][ChatbotService]', 'User intent (raw):', userMessage.slice(0, 200));

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a specialized AI assistant for the DST Platform, helping Meijer users on the Company Admin Dashboard.

You have access to TWO data sources:
1. DASHBOARD ANALYTICS - Detailed data from dashboard cards (active users, MSI devices, property inspections, critical inspections, reports)
2. WORK ORDER SUMMARY - Aggregated statistics from work orders (not individual work orders)

Current Context:
- User: ${context.user?.name || 'User'} (${context.user?.companyName || 'Meijer'})
- Date Range: ${moment(context.dateRange?.start).format('MMM D, YYYY')} to ${moment(context.dateRange?.end).format('MMM D, YYYY')}
- Favorites: ${context.favorites?.count > 0 ? `${context.favorites.count} properties (${context.favorites.propertyNames.slice(0, 5).join(', ')}${context.favorites.count > 5 ? '...' : ''})` : 'None'}
- Property Groups: ${context.propertyLists?.count > 0 ? `${context.propertyLists.count} groups (${context.propertyLists.lists.join(', ')})` : 'None'}

=== WORK ORDER SUMMARY ===
${context._dataLoading
  ? `Work order summary is currently LOADING. Counts, costs, and status breakdowns are NOT yet available — do not quote numbers from prior turns, and do not assume any cached values are still valid. Tell the user their data is still loading and ask them to wait a moment before re-asking.`
  : `- Total Work Orders: ${summary.total || 0}${summary.total === 0 ? '\nNOTE: Zero work orders were found for this date range. This is confirmed (not a loading issue).' : ''}
- Compressor Failures Detected: ${summary.compressorFailures || 0}
- Status Breakdown: ${Object.entries(summary.statusBreakdown || {}).map(([s, c]) => `${s}: ${c}`).join(', ') || 'N/A'}
- Total Costs: $${(summary.totalCost || 0).toLocaleString()}
NOTE: On this page you have summary statistics only, not individual work order details. If the user needs to drill into specific work orders, suggest they navigate to another page where full work order details are available.`}

=== DASHBOARD ANALYTICS ===
${this.formatDashboardDataForPrompt(dashboardData, dataFetched)}

Guidelines:
- Be conversational, helpful, and professional
- Reference specific data points when available
- Do NOT use emojis — maintain a professional business tone
- Keep responses concise (under 400 words unless asked for details)
- If asked about data that hasn't been loaded yet, explain they need to click "Get Results" on that dashboard card
- If asked for individual work order details (specific WO numbers, individual assignees, etc.), explain that on the dashboard page you have summary statistics only, and suggest navigating to another page for detailed work order analysis
- Suggest follow-up questions that leverage having both data sources available
- Cross-reference data across cards and between work orders and dashboard data when useful

Output Formatting for the Chat Widget (~360px wide):
The chat widget is narrow. Structure responses so they are skimmable, scannable, and parsable — but PRESERVE full content. Do NOT abbreviate or truncate property names, equipment names, evaluation descriptions, user names, or any other detail fields.

When listing records (properties, users, MSI devices, reports, etc.), use this multi-line format with a clear header line per item:

  1. **Property 0125A** · 95 critical points · Health 92%
     Last Technician: John Smith (Apr 28)
     Critical Evaluations: Compressor noise, Refrigerant leak, Voltage drop

  2. **Property 0142A** · 12 critical points · Health 78%
     Last Technician: Sarah Lee (Apr 27)
     Critical Evaluations: Compressor failure

Formatting rules:
- Header line per item: numbered, **bold primary identifier**, key stats — separated by interpunct (·) so users can scan vertically without reading every field
- Full content on labeled lines below (Last Technician:, Critical Evaluations:, etc.) — never abbreviate or truncate
- Blank line between items in a list for visual separation
- Drop the year from dates when the date range is established in context above (use "Apr 28" not "Apr 28, 2026")
- For prose answers: short paragraphs (3–4 lines max), prefer lists over comma-separated runs of items, only bold what genuinely matters (over-bolding kills scannability)
- NEVER use markdown tables — they don't render well in the narrow widget; use the multi-line item format above
- Use professional formatting: bullet points, bold (**text**), numbered lists, section headers
- Format numbers with commas (e.g., 1,234 not 1234)
- Format currency properly (e.g., $1,234.56)`;
  }

  /**
   * Build system prompt for regular messages (work order focus, no view mode)
   */
  static buildSystemPrompt(context, userMessage) {
    const compressorFailures = (context.workOrders || []).filter(wo => wo.compressor_failure_detected).length;

    // Calculate status breakdown
    const statusCounts = (context.workOrders || []).reduce((acc, wo) => {
      const status = wo.status_id || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const totalCost = (context.workOrders || []).reduce((sum, wo) => sum + (parseFloat(wo.costs_total_value) || 0), 0);

    console.log('[Mike][ChatbotService]', 'buildSystemPrompt()', {
      user: `${context.user.name} (${context.user.companyName})`,
      dateRange: `${moment(context.dateRange.start).format('YYYY-MM-DD')} -> ${moment(context.dateRange.end).format('YYYY-MM-DD')}`,
      totalWorkOrders: context.totalWorkOrders,
      compressorFailures,
      statusBreakdown: statusCounts
    });
    if (userMessage) console.log('[Mike][ChatbotService]', 'User intent (raw):', userMessage.slice(0, 200));

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a specialized AI assistant for analyzing Meijer work orders and compressor failures in the DST Platform. You specialize in work order and compressor failure analysis — if asked about topics outside this scope, politely explain your focus.

Current Context:
- User: ${context.user.name} (${context.user.companyName})
- Date Range: ${moment(context.dateRange.start).format('MMM D, YYYY')} to ${moment(context.dateRange.end).format('MMM D, YYYY')}
${context._dataLoading
  ? `\nWORK ORDER DATA: LOADING
Work order data is currently being fetched for the date range above. Counts, costs, status breakdowns, and individual records are NOT yet available — do not quote numbers from prior turns, and do not assume any cached values are still valid. Tell the user their data is still loading and ask them to wait a moment before re-asking.`
  : `- Total Work Orders: ${context.totalWorkOrders}${context.totalWorkOrders === 0 ? '\nNOTE: Zero work orders were found for this date range. This is confirmed (not a loading issue). If the user asks about work orders, confirm that none exist in this date range and suggest adjusting the date range.' : ''}
- Compressor Failures Detected: ${compressorFailures}
- Status Breakdown: ${Object.entries(statusCounts).map(([status, count]) => `${status}: ${count}`).join(', ')}
- Total Costs: $${totalCost.toLocaleString()}

Work Order Data:
${this.formatContextForPrompt(context)}`}

Guidelines:
- Be conversational, helpful, and professional
- Reference specific data points (work order numbers, property numbers, dates, costs)
- Do NOT use emojis — maintain a professional business tone
- Keep responses concise (under 400 words unless asked for details)
- Prioritize compressor failure information when relevant
- If asked about data outside the date range, explain the limitation
- Suggest follow-up questions about compressor failures or work order trends
- When listing multiple items, order by highest cost first unless the user asks for a different ordering
- For "summary" requests, provide a well-structured overview with key insights on compressor failures

Output Formatting for the Chat Widget (~360px wide):
The chat widget is narrow. Structure responses so they are skimmable, scannable, and parsable — but PRESERVE full content. Do NOT abbreviate or truncate equipment names, failure descriptions, notes, or any other detail fields.

When listing work orders, use this multi-line format with a clear header line per item:

  1. **MEJR032008202** · 0032A · Apr 29
     Equipment: RACK1 - RACK SYSTEM REFRIGERATION-PARALLEL - Compressor 1 tripping breaker
     Failure: Compressor #1 replaced due to bad contractor

  2. **MEJR285005056** · 0285A · Apr 29
     Equipment: RACK4 - RACK SYSTEM REFRIGERATION-PROTOCOL
     Failure: Compressor has open windings

Formatting rules:
- Header line per item: numbered, **bold WO #**, property, date — separated by interpunct (·) so users can scan vertically without reading every field
- Full content on labeled lines below (Equipment:, Failure:, Notes:, Cost: as relevant) — never abbreviate or truncate
- Blank line between items in a list for visual separation
- Drop the year from dates when the date range is established in context above (use "Apr 29" not "Apr 29, 2026")
- For prose answers: short paragraphs (3–4 lines max), prefer lists over comma-separated runs of items, only bold what genuinely matters (over-bolding kills scannability)
- NEVER use markdown tables — they don't render well in the narrow widget; use the multi-line item format above
- Use professional formatting: bullet points, bold (**text**), numbered lists, section headers
- Format numbers with commas (e.g., 1,234 not 1234)
- Format currency properly (e.g., $1,234.56)
- Use exact status names (Open, New, Completed, Paused, Cancelled, InProgress) — never collapse to "open/closed"`;
  }

  /**
   * Build system prompt for non-Meijer welcome message (Company Admin Dashboard)
   */
  static buildNonMeijerWelcomeSystemPrompt(context) {
    const userName = context.user?.name?.split(' ')[0] || 'there';
    const companyName = context.user?.companyName || 'your company';

    // Check what data has been fetched
    const dataFetched = context.dashboardDataFetched || {};
    const anyDataFetched = Object.values(dataFetched).some(v => v);

    // Format favorites info
    const favoritesInfo = context.favorites?.count > 0
      ? `${context.favorites.count} favorite properties: ${context.favorites.propertyNames.slice(0, 5).join(', ')}${context.favorites.count > 5 ? '...' : ''}`
      : 'No favorite properties set';

    // Format property lists info
    const propertyListsInfo = context.propertyLists?.count > 0
      ? `${context.propertyLists.count} property lists: ${context.propertyLists.lists.join(', ')}`
      : 'No property lists available';

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a helpful AI assistant for DST (Diagnostic Solutions Technologies) Platform.

You are assisting a user from ${companyName} on the Company Admin Dashboard.

Generate a warm welcome message that:
1. Greets the user by first name (${userName}) and introduces yourself as their DST AI Assistant
2. Shows what you know about them (company, role, date range, favorites, property lists)
3. ${anyDataFetched ? 'Shows which dashboard data has been loaded and which still needs loading' : 'Explains they need to click "Get Results" on dashboard cards to load data for analysis'}
4. Lists what kinds of questions you can answer once data is loaded

User Context:
- Name: ${context.user?.name || 'User'}
- Email: ${context.user?.email || 'N/A'}
- Role: ${context.user?.role || 'User'}
- Company: ${companyName}
- Date Range: ${moment(context.dateRange?.start).format('MMM D, YYYY')} to ${moment(context.dateRange?.end).format('MMM D, YYYY')}
- ${favoritesInfo}
- ${propertyListsInfo}

Data Loading Status:
- Active Users: ${dataFetched.activeUsers ? 'Loaded' : 'Not loaded yet'}
- MSI Used: ${dataFetched.msiUsed ? 'Loaded' : 'Not loaded yet'}
- Properties Inspected: ${dataFetched.propertiesInspected ? 'Loaded' : 'Not loaded yet'}
- Critical Inspections: ${dataFetched.criticalInspections ? 'Loaded' : 'Not loaded yet'}
- Reports: ${dataFetched.reports ? 'Loaded' : 'Not loaded yet'}
- Tasking Sheets: ${dataFetched.taskingSheets ? 'Loaded' : 'Not loaded yet'}
- All Company Properties: ${dataFetched.allCompanyProperties ? 'Loaded' : 'Not loaded yet'}

Guidelines:
- Be warm, friendly, and professional
- Use clean formatting with sections and bullet points
- NEVER use markdown tables - they don't render well in chat interfaces
- Use checkmarks (✓) for loaded data and circles (○) for not loaded
- Do NOT use emojis - maintain a professional business tone
- Keep under 250 words
- End with an inviting question about what they'd like to explore

Format Structure:
Hi [First Name]! I'm Mike, your DST AI Assistant.

**What I Know About You:**
━━━━━━━━━━━━━━━━━━━━━━━━
• Company: [company]
• Role: [role]
• Date Range: [date range]
• Favorites: [favorites info]
• Property Groups: [property groups info]

**${anyDataFetched ? 'Available Data:' : 'Getting Started:'}**
[Either show data status with ✓/○ OR explain to click Get Results]

[Questions you can answer about the loaded data OR what to do next]`;
  }

  /**
   * Build system prompt for non-Meijer regular messages (Company Admin Dashboard)
   */
  static buildNonMeijerSystemPrompt(context, userMessage) {
    const companyName = context.user?.companyName || 'the company';
    const dataFetched = context.dashboardDataFetched || {};
    const dashboardData = context.dashboardData || {};

    console.log('[Mike][ChatbotService]', 'buildNonMeijerSystemPrompt()', {
      user: `${context.user?.name} (${companyName})`,
      dateRange: `${moment(context.dateRange?.start).format('YYYY-MM-DD')} -> ${moment(context.dateRange?.end).format('YYYY-MM-DD')}`,
      favorites: context.favorites?.count || 0,
      propertyLists: context.propertyLists?.count || 0,
      dataFetched
    });
    if (userMessage) console.log('[Mike][ChatbotService]', 'User intent (raw):', userMessage.slice(0, 200));

    return `${this.getAntiHallucinationPreamble(context)}${this.getCriticalBehavioralRules()}You are Mike, a helpful AI assistant for DST (Diagnostic Solutions Technologies) Platform, assisting a user from ${companyName} on the Company Admin Dashboard. You specialize in dashboard data analysis — if asked about topics outside this scope, politely explain your focus.

User Context:
- Name: ${context.user?.name || 'User'}
- Email: ${context.user?.email || 'N/A'}
- Role: ${context.user?.role || 'User'}
- Company: ${companyName}
- Date Range: ${moment(context.dateRange?.start).format('MMM D, YYYY')} to ${moment(context.dateRange?.end).format('MMM D, YYYY')}
- Favorites: ${context.favorites?.count > 0 ? `${context.favorites.count} properties (${context.favorites.propertyNames.slice(0, 5).join(', ')}${context.favorites.count > 5 ? '...' : ''})` : 'None'}
- Property Groups: ${context.propertyLists?.count > 0 ? `${context.propertyLists.count} groups (${context.propertyLists.lists.join(', ')})` : 'None'}

Data Loading Status:
- Active Users: ${dataFetched.activeUsers ? 'LOADED' : 'NOT LOADED'}
- MSI Used: ${dataFetched.msiUsed ? 'LOADED' : 'NOT LOADED'}
- Properties Inspected: ${dataFetched.propertiesInspected ? 'LOADED' : 'NOT LOADED'}
- Critical Inspections: ${dataFetched.criticalInspections ? 'LOADED' : 'NOT LOADED'}
- Reports: ${dataFetched.reports ? 'LOADED' : 'NOT LOADED'}
- Tasking Sheets: ${dataFetched.taskingSheets ? 'LOADED' : 'NOT LOADED'}
- All Company Properties: ${dataFetched.allCompanyProperties ? 'LOADED' : 'NOT LOADED'}

${this.formatDashboardDataForPrompt(dashboardData, dataFetched)}

Guidelines:
- Be conversational, helpful, and professional
- Reference specific data points when available
- Do NOT use emojis — maintain a professional business tone
- Keep responses concise (under 400 words unless asked for details)
- If asked about data that hasn't been loaded yet, politely explain they need to click "Get Results" on that card first
- Suggest follow-up questions to help users explore their data
- For "summary" requests, provide a well-structured overview of loaded data
- Cross-reference data across cards when useful

Output Formatting for the Chat Widget (~360px wide):
The chat widget is narrow. Structure responses so they are skimmable, scannable, and parsable — but PRESERVE full content. Do NOT abbreviate or truncate property names, equipment names, evaluation descriptions, user names, or any other detail fields.

When listing records (properties, users, MSI devices, reports, etc.), use this multi-line format with a clear header line per item:

  1. **Property 0125A** · 95 critical points · Health 92%
     Last Technician: John Smith (Apr 28)
     Critical Evaluations: Compressor noise, Refrigerant leak, Voltage drop

  2. **Property 0142A** · 12 critical points · Health 78%
     Last Technician: Sarah Lee (Apr 27)
     Critical Evaluations: Compressor failure

Formatting rules:
- Header line per item: numbered, **bold primary identifier**, key stats — separated by interpunct (·) so users can scan vertically without reading every field
- Full content on labeled lines below (Last Technician:, Critical Evaluations:, etc.) — never abbreviate or truncate
- Blank line between items in a list for visual separation
- Drop the year from dates when the date range is established in context above (use "Apr 28" not "Apr 28, 2026")
- For prose answers: short paragraphs (3–4 lines max), prefer lists over comma-separated runs of items, only bold what genuinely matters (over-bolding kills scannability)
- NEVER use markdown tables — they don't render well in the narrow widget; use the multi-line item format above
- Use professional formatting: bullet points, bold (**text**), numbered lists, section headers
- Format numbers with commas (e.g., 1,234 not 1234)
- Format currency properly (e.g., $1,234.56)`;
  }

  /**
   * Budget-aware item formatting. Iterates items, formats each with formatFn,
   * and stops when char budget is exceeded.
   * @returns {string} Formatted text with "... and N more" if truncated
   */
  static _budgetTruncate(items, formatFn, budgetChars) {
    const lines = [];
    let usedChars = 0;
    for (let i = 0; i < items.length; i++) {
      const line = formatFn(items[i]);
      if (usedChars + line.length > budgetChars && lines.length > 0) {
        lines.push(`... and ${items.length - i} more items (${items.length} total)`);
        break;
      }
      lines.push(line);
      usedChars += line.length;
    }
    return { text: lines.join('\n'), included: Math.min(lines.length, items.length), total: items.length };
  }

  /**
   * Format dashboard data for system prompt
   * Includes enriched details for comprehensive AI analysis
   * Uses context budget to dynamically manage data size instead of fixed item caps
   */
  static formatDashboardDataForPrompt(dashboardData, dataFetched) {
    const sections = [];

    // Count active sections to allocate budget proportionally
    const activeCards = ['activeUsers', 'msiUsed', 'propertiesInspected', 'criticalInspections', 'reports', 'taskingSheets', 'allCompanyProperties']
      .filter(card => dataFetched[card] && dashboardData[card]);
    const sectionBudget = activeCards.length > 0
      ? Math.floor((this.CONTEXT_BUDGET - 20000) / activeCards.length)
      : 50000;

    // Active Users - with enriched details
    if (dataFetched.activeUsers && dashboardData.activeUsers) {
      const activeUsers = dashboardData.activeUsers;
      const totalActiveUsers = activeUsers.details?.length || 0;
      const totalAllUsers = activeUsers.byRole?.reduce((sum, r) => sum + (r.totalCount || 0), 0) || 0;
      const roleBreakdown = activeUsers.byRole?.map(r => `${r.roleTitle}: ${r.activeCount} active of ${r.totalCount} total`).join(', ') || 'N/A';

      const enrichedDetails = activeUsers.enrichedDetails || [];
      let userDetailsSection = '';
      if (enrichedDetails.length > 0) {
        const { text, included } = this._budgetTruncate(enrichedDetails, u =>
          `- ${u.fullName || 'N/A'} | Role: ${u.role || 'N/A'} | Last Login: ${u.lastLogin ? moment(u.lastLogin).format('MMM D, YYYY') : 'N/A'} | App: ${u.appVersion || 'N/A'} | MSI Serial: ${u.msiSerial || 'N/A'} | MSI Connection: ${u.msiConnection ? moment(u.msiConnection).format('MMM D, YYYY HH:mm') : 'N/A'} | Device: ${u.deviceType || 'N/A'} | Property: ${u.recentProperty || 'N/A'} | Report: ${u.reportLink || 'N/A'}`,
          sectionBudget
        );
        userDetailsSection = `\n\nDETAILED USER ACTIVITY (${included} of ${totalActiveUsers}):\n${text}`;
      } else {
        userDetailsSection = `\n- User Names: ${activeUsers.details?.map(u => `${u.full_name} (${u.roles?.[0]?.title || 'N/A'})`).join(', ')}`;
      }

      sections.push(`ACTIVE USERS DATA:
- Total Active Users: ${totalActiveUsers} (out of ${totalAllUsers} total users)
- By Role: ${roleBreakdown}${userDetailsSection}`);
    }

    // MSI Used - with enriched details
    if (dataFetched.msiUsed && dashboardData.msiUsed) {
      const msiData = dashboardData.msiUsed;

      let msiSection = `MSI USED DATA:
- Unique MSI Devices Used in Period: ${msiData.count || 0}
- Total Company MSI Devices (Zoho): ${msiData.totalDevices || 'N/A'}`;

      // Enriched Used MSI details
      const enrichedMsi = msiData.enrichedDetails || [];
      if (enrichedMsi.length > 0) {
        const { text, included } = this._budgetTruncate(enrichedMsi, m =>
          `- Serial: ${m.serial} | Users: ${m.users.join(', ')} | Connections: ${m.connectionCount} | Last Connection: ${m.lastConnection ? moment(m.lastConnection).format('MMM D, YYYY HH:mm') : 'N/A'} | Device Types: ${m.deviceTypes.join(', ') || 'N/A'} | MSI Version: ${m.latestMsiVersion || 'N/A'}`,
          Math.floor(sectionBudget * 0.5)
        );
        msiSection += `\n\nMSI DEVICES USED IN PERIOD (${included} of ${enrichedMsi.length}):\n${text}`;
      } else if (msiData.details?.length > 0) {
        // Fallback to simple tuples
        msiSection += `\n- MSI Details: ${msiData.details.map(m => `${m[0]} (User: ${m[1] || 'Unknown'})`).join(', ')}`;
      }

      // Zoho device inventory
      const zohoDevices = msiData.zohoDevices || [];
      if (zohoDevices.length > 0) {
        const { text, included } = this._budgetTruncate(zohoDevices, d =>
          `- Serial: ${d.serial} | Owner: ${d.owner || 'N/A'} | Status: ${d.status || 'N/A'} | Type: ${d.type || 'N/A'} | Used in Period: ${d.isUsed ? 'Yes' : 'No'}`,
          Math.floor(sectionBudget * 0.5)
        );
        msiSection += `\n\nALL COMPANY MSI DEVICES (Zoho Inventory, ${included} of ${zohoDevices.length}):\n${text}`;
      }

      sections.push(msiSection);
    }

    // Properties Inspected - with enriched details
    if (dataFetched.propertiesInspected && dashboardData.propertiesInspected) {
      const propData = dashboardData.propertiesInspected;
      const percentage = propData.total > 0 ? ((propData.count / propData.total) * 100).toFixed(1) : 0;

      const enrichedDetails = propData.enrichedDetails || [];
      const totalProperties = propData.count || 0;

      let propertyDetailsSection = '';
      if (enrichedDetails.length > 0) {
        const { text, included } = this._budgetTruncate(enrichedDetails, p =>
          `- ${p.propertyName || 'N/A'} | Critical Points: ${p.criticalPoint || 0} | Critical Evals: ${p.criticalEvaluations || 0} | Inspected: ${p.inspected || 0}/${p.totalPoints || 0} | ${p.percentInspected || 0}% | Health: ${p.healthScore || 'N/A'}% | Technician: ${p.lastUploader || 'N/A'} | Last Inspection: ${p.lastInspection || 'N/A'}`,
          sectionBudget
        );
        propertyDetailsSection = `\n\nDETAILED PROPERTY INSPECTIONS (${included} of ${totalProperties}):\n${text}`;
      } else {
        propertyDetailsSection = `\n- Property Names: ${propData.details?.map(p => p.property_name || p.name || 'Unknown').join(', ')}`;
      }

      sections.push(`PROPERTIES INSPECTED DATA:
- Properties Inspected: ${propData.count || 0} of ${propData.total || 0} (${percentage}%)${propertyDetailsSection}`);
    }

    // Critical Inspections - with enriched details
    if (dataFetched.criticalInspections && dashboardData.criticalInspections) {
      const critData = dashboardData.criticalInspections;

      let criticalPointsSection = '';
      let criticalEvalsSection = '';

      // Critical Points Details
      const criticalPointsDetails = critData.criticalPointsDetails || [];
      if (criticalPointsDetails.length > 0) {
        const { text, included } = this._budgetTruncate(criticalPointsDetails, p =>
          `- ${p.property || 'N/A'} | Equipment: ${p.rootComp || 'N/A'} | Point: ${p.pointName || 'N/A'} | Signatures: ${p.soundSignatures || 'None'} | Actions: ${p.actions || 'None'} | Date: ${p.inspectionDate ? moment(p.inspectionDate).format('MMM D, YYYY') : 'N/A'} | Sound Energy: ${p.soundEnergy || 'N/A'} | Evaluation: ${p.evaluation || 'N/A'} | Sound Evaluation (AI): ${p.soundEvaluator || 'N/A'} | Technician: ${p.technician || 'N/A'}`,
          Math.floor(sectionBudget * 0.5)
        );
        criticalPointsSection = `\n\nCRITICAL POINTS DETAILS (${included} of ${criticalPointsDetails.length}):\n${text}`;
      }

      // Critical Evaluations Details
      const criticalEvalsDetails = critData.criticalEvalsDetails || [];
      if (criticalEvalsDetails.length > 0) {
        const { text, included } = this._budgetTruncate(criticalEvalsDetails, e =>
          `- ${e.property || 'N/A'} | Equipment: ${e.equipment || 'N/A'} | Date: ${e.createdAt ? moment(e.createdAt).format('MMM D, YYYY') : 'N/A'} | Sound Evaluator: ${e.soundEvaluator || 'N/A'} | Evaluation: ${e.evaluation || 'N/A'} | Tags: ${(e.mpeTagNames || []).join(', ') || 'None'} | Actions: ${(e.mpeActionNames || []).join(', ') || 'None'}`,
          Math.floor(sectionBudget * 0.5)
        );
        criticalEvalsSection = `\n\nCRITICAL EVALUATIONS DETAILS (${included} of ${criticalEvalsDetails.length}):\n${text}`;
      }

      sections.push(`CRITICAL INSPECTIONS DATA:
- Critical Red Points: ${critData.redPoints || 0}
- Critical Evaluations: ${critData.evaluations || 0}
- Properties with Point Data: ${critData.pointInfos?.length || 0}
- Properties with Values Data: ${critData.vals?.length || 0}
- Properties with FIGS Evaluations: ${critData.figsEvaluations?.length || 0}${criticalPointsSection}${criticalEvalsSection}`);
    }

    // Reports - with enriched details
    if (dataFetched.reports && dashboardData.reports) {
      const reportData = dashboardData.reports;
      const totalReports = reportData.count || 0;

      const enrichedDetails = reportData.enrichedDetails || [];
      let reportDetailsSection = '';
      if (enrichedDetails.length > 0) {
        const { text, included } = this._budgetTruncate(enrichedDetails, r =>
          `- ${r.reportType || 'Report'} | Date: ${r.createdAt ? moment(r.createdAt).format('MMM D, YYYY') : 'N/A'} | User: ${r.userName || 'N/A'} | Property: ${r.propertyName || 'N/A'} | Link: ${r.link || 'N/A'}`,
          sectionBudget
        );
        reportDetailsSection = `\n\nDETAILED REPORTS (${included} of ${totalReports}):\n${text}`;
      } else {
        reportDetailsSection = `\n- Report Details: ${reportData.details?.map(r => `${r.reportType || 'Report'} (Created: ${r.createdAt ? moment(r.createdAt).format('MMM D, YYYY') : 'N/A'})`).join(', ')}`;
      }

      sections.push(`REPORTS DATA:
- Total Reports Generated: ${reportData.count || 0}${reportDetailsSection}`);
    }

    // Tasking Sheets
    if (dataFetched.taskingSheets && dashboardData.taskingSheets) {
      const tsData = dashboardData.taskingSheets;
      let tsSection = `PROPERTIES WITH TASKING SHEETS DATA:
- Properties with Tasking Sheet Activity: ${tsData.count || 0} of ${tsData.total || 0}`;

      if (tsData.details?.length > 0) {
        const { text, included } = this._budgetTruncate(tsData.details, t =>
          `- ${t.propertyName} | Last Check: ${t.lastCheckDate} | User: ${t.lastCheckUser} | Checks in Period: ${t.checksInPeriod}`,
          sectionBudget
        );
        tsSection += `\n\nTASKING SHEET DETAILS (${included} of ${tsData.details.length}):\n${text}`;
      }
      sections.push(tsSection);
    }

    // All Company Properties (Denominator)
    if (dataFetched.allCompanyProperties && dashboardData.allCompanyProperties) {
      const acpData = dashboardData.allCompanyProperties;
      // Cross-reference with propertiesInspected to compute inspectedInPeriod
      const inspectedNames = new Set(
        (dashboardData.propertiesInspected?.enrichedDetails || []).map(p => p.propertyName)
      );

      let acpSection = `ALL COMPANY PROPERTIES (DENOMINATOR DATA):
- Total Properties: ${acpData.total || 0}`;

      if (acpData.properties?.length > 0) {
        const { text, included } = this._budgetTruncate(acpData.properties, p =>
          `- ${p.name} | Last Report: ${p.lastReportRun ? moment(p.lastReportRun).format('MMM D, YYYY') : 'N/A'} | Last Evaluation: ${p.lastEvaluation ? moment(p.lastEvaluation).format('MMM D, YYYY') : 'N/A'} | Last Upload: ${p.lastUpload ? moment(p.lastUpload).format('MMM D, YYYY') : 'N/A'} | Inspected: ${inspectedNames.has(p.name) ? 'Yes' : 'No'}`,
          sectionBudget
        );
        acpSection += `\n\nFULL PROPERTY LIST (${included} of ${acpData.properties.length}):\n${text}`;
      }
      sections.push(acpSection);
    }

    if (sections.length === 0) {
      return 'DASHBOARD DATA: No data has been loaded yet. User needs to click "Get Results" on dashboard cards.';
    }

    return `DASHBOARD DATA:\n${sections.join('\n\n')}`;
  }


  /**
   * Format work order context for prompts.
   *
   * Pre-segments compressor failures into a dedicated section so the model
   * does not need to scan the full WO list to find them. This is the structural
   * fix for the "list 17 of 21 compressor failures" failure mode — the model
   * reads the pre-filtered section directly instead of attempting an exhaustive
   * scan across hundreds of records.
   */
  static formatContextForPrompt(context) {
    if (!context.workOrders || context.workOrders.length === 0) {
      console.log('[Mike][ChatbotService]', 'formatContextForPrompt: no work orders');
      return 'No work orders available in current view.';
    }

    // Loose truthy check — resilient if the backend ever returns "true" string instead of boolean
    const isCompressorFailure = (wo) => wo.compressor_failure_detected === true || wo.compressor_failure_detected === 'true';
    const compressorFailures = context.workOrders.filter(isCompressorFailure);
    const otherWorkOrders = context.workOrders.filter(wo => !isCompressorFailure(wo));

    const sections = [];

    if (compressorFailures.length > 0) {
      const formattedFailures = compressorFailures.map(wo => this.formatWorkOrderForPrompt(wo));
      sections.push(`=== COMPRESSOR FAILURE WORK ORDERS (${compressorFailures.length} of ${compressorFailures.length} — COMPLETE LIST, NOT TRUNCATED) ===
This section contains EVERY work order where compressor_failure_detected = true. When the user asks to list, filter, count, or analyze compressor failures, read directly from this section — do not scan the ALL OTHER WORK ORDERS section below for additional matches, none exist.

${formattedFailures.join('\n\n')}`);
    } else {
      sections.push(`=== COMPRESSOR FAILURE WORK ORDERS (0 of 0) ===
No work orders in the current date range have compressor_failure_detected = true.`);
    }

    if (otherWorkOrders.length > 0) {
      const formattedOthers = otherWorkOrders.map(wo => this.formatWorkOrderCompact(wo));
      sections.push(`=== ALL OTHER WORK ORDERS (${otherWorkOrders.length} of ${otherWorkOrders.length}) ===
Work orders below have compressor_failure_detected = false. Shown in compact format — all fields preserved but condensed for efficiency. If the user asks for full detail on a specific WO, expand it conversationally from the data here.

${formattedOthers.join('\n\n')}`);
    }

    const compressorChars = sections[0]?.length || 0;
    const othersChars = sections[1]?.length || 0;
    const result = sections.join('\n\n');
    console.log('[Mike][ChatbotService]', `formatContextForPrompt: total chars=${result.length.toLocaleString()} (compressorFailures section=${compressorChars.toLocaleString()}, others section=${othersChars.toLocaleString()}); compressorFailures=${compressorFailures.length}, others=${otherWorkOrders.length}`);
    return result;
  }


  /**
   * Format a single work order for the prompt (compressor-focused, full detail).
   * Used in the COMPRESSOR FAILURE WORK ORDERS section.
   */
  static formatWorkOrderForPrompt(workOrder) {
    const truncate = (text, maxLength = 150) => {
      if (!text) return 'N/A';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Build compressor failure details
    let compressorDetails = 'No';
    if (workOrder.compressor_failure_detected) {
      compressorDetails = `Yes (Confidence: ${workOrder.compressor_failure_confidence || 'N/A'})`;
      if (workOrder.compressor_failure_reasoning) {
        compressorDetails += `\n  Reasoning: ${truncate(workOrder.compressor_failure_reasoning, 200)}`;
      }
    }

    return `Work Order #${workOrder.number} (Property: ${workOrder.property_number})
  Status: ${workOrder.status_id || 'Unknown'}
  Created: ${workOrder.dt_created ? moment(workOrder.dt_created).format('MMM D, YYYY') : 'N/A'}
  Assignee: ${workOrder.assignee_screen_name || 'N/A'}
  Task: ${truncate(workOrder.task_refinement, 200)}
  Root Component: ${workOrder.root_comp || 'N/A'}
  Cost: $${parseFloat(workOrder.costs_total_value || 0).toFixed(2)}
  Compressor Failure: ${compressorDetails}
  Notes: ${truncate(workOrder.note_text, 150)}
  Completion Notes: ${truncate(workOrder.completion_note_text, 150)}`;
  }

  /**
   * Compact format for a single work order — preserves all fields, condensed layout.
   * Used in the ALL OTHER WORK ORDERS section so non-compressor-failure WOs occupy
   * less attention budget while remaining queryable for follow-up questions.
   */
  static formatWorkOrderCompact(workOrder) {
    const truncate = (text, maxLength = 150) => {
      if (!text) return null;
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Line 1: identifiers + scalar fields, interpunct-separated. Omit segments for missing values.
    const line1Parts = [`#${workOrder.number || 'N/A'}`];
    if (workOrder.property_number) line1Parts.push(workOrder.property_number);
    if (workOrder.status_id) line1Parts.push(workOrder.status_id);
    if (workOrder.dt_created) line1Parts.push(moment(workOrder.dt_created).format('MMM D, YYYY'));
    line1Parts.push(`$${parseFloat(workOrder.costs_total_value || 0).toFixed(2)}`);
    if (workOrder.assignee_screen_name) line1Parts.push(workOrder.assignee_screen_name);
    if (workOrder.root_comp) line1Parts.push(`Root: ${workOrder.root_comp}`);
    const line1 = line1Parts.join(' · ');

    // Line 2: Task (full text, existing 200-char cap)
    const task = truncate(workOrder.task_refinement, 200);
    const line2 = task ? `  Task: ${task}` : null;

    // Line 3: Notes + Completion (each existing 150-char cap), joined with ·
    const notes = truncate(workOrder.note_text, 150);
    const completion = truncate(workOrder.completion_note_text, 150);
    const line3Parts = [];
    if (notes) line3Parts.push(`Notes: ${notes}`);
    if (completion) line3Parts.push(`Completion: ${completion}`);
    const line3 = line3Parts.length > 0 ? `  ${line3Parts.join(' · ')}` : null;

    return [line1, line2, line3].filter(Boolean).join('\n');
  }

  static async sendMessageStreaming(userMessage, context, conversationHistory = [], isWelcome = false, onChunk) {
    const result = await this.sendMessage(userMessage, context, conversationHistory, isWelcome);
    if (result.reply && onChunk) onChunk(result.reply);
    return result;
  }
}

export default ChatbotService;
